import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode, useEffect, useRef, type ReactNode } from 'react';
import { I18nProvider, UNSTABLE_ToastQueue as ToastQueue } from 'react-aria-components';
import { flushSync } from 'react-dom';
import ReactDOM from 'react-dom/client';
import { AuthProvider, useAuth } from 'react-oidc-context';

import { LOCALE } from '@/constants/locale';
import { OIDC_CONFIG } from '@/constants/oidc';
import { LOCAL_STORAGE } from '@/constants/storage';
import { useMinDelay } from '@/hooks/use-min-delay.hook';
import { HttpException } from '@/libs/openapi';
import {
  Link,
  TOAST_QUEUE_CONFIG,
  TOAST_TIMEOUT,
  useLocalStorage,
  type DialogProps,
  type SnackbarProps,
  type TextButtonProps,
} from '@/libs/serenity-ui';
import reportWebVitals from '@/report-web-vitals';
import { organizationQueries } from '@/resources/organization/organization.api';
import { userQueries } from '@/resources/user/user.api';
import { routeTree, type FileRouteTypes } from '@/routeTree.gen';
import { timezone } from '@/utils/timezone.util';

import '@/libs/serenity-ui/index.css';

export const toastQueue = new ToastQueue<{
  message: string;
  actionProps?: TextButtonProps;
  variant: SnackbarProps['variant'];
}>(TOAST_QUEUE_CONFIG);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      retry: (failureCount, error) => {
        if (error instanceof HttpException && (error.response?.status === 403 || error.response?.status === 401)) {
          return false;
        }

        return failureCount < 3;
      },
    },
    mutations: {
      onError: (error) => {
        if (error instanceof HttpException && !error.isGlobal) {
          return;
        }

        toastQueue.add({ message: error.message, variant: 'error' }, { timeout: TOAST_TIMEOUT });
      },
    },
  },
});

const router = createRouter({
  routeTree,
  context: {
    queryClient,
    toastQueue,
    user: undefined!,
    organization: undefined!,
    info: undefined!,
    theme: undefined!,
    setTheme: undefined!,
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  notFoundMode: 'root',
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }

  interface FilterListItemData {
    id: string;
    filterId: string;
    filterValue?: unknown;
    category: string;
    value: string;
  }

  interface StaticDataRouteOption {
    list?: {
      heading: string;
      slots?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        providerComponent?: (...props: any) => ReactNode;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        toolbarLeftComponent?: (...props: any) => ReactNode;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        toolbarActionsComponent?: (...props: any) => ReactNode;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        topBarComponent?: (...props: any) => ReactNode;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        bottomBarComponent?: (...props: any) => ReactNode;
      };
      topBarComponent?: ReactNode;
      filters?: DialogProps['children'];
      actions?: ReactNode;
      secondaryActions?: ReactNode;
      options?: {
        /**
         * The key used to identify the search filter for this list view.
         *
         * When specified, a search bar and filter controls are rendered in the list header.
         * If omitted, neither the search bar nor any filters will be rendered.
         */
        searchFilter?: string;
      }
    };
    form?: {
      heading: string | (() => ReactNode);
      toolbarLayout: 'centered' | 'full-width';
      pageType?: string;
      goBackFallbackPath?: FileRouteTypes['fullPaths'];
      slots?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        providerComponent?: (...props: any) => ReactNode;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        topBarComponent?: (...props: any) => ReactNode;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        actionsComponent?: (...props: any) => ReactNode;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        statusComponent?: (...props: any) => ReactNode;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        idComponent?: (...props: any) => ReactNode;
      };
    };
    details?: {
      heading: string | (() => ReactNode);
      pageType?: string;
      slots?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        topBarComponent?: (...props: any) => ReactNode;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        idComponent?: (...props: any) => ReactNode;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        statusComponent?: (...props: any) => ReactNode;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        actionsComponent?: (...props: any) => ReactNode;
      };
    }
    minimal?: {
      showLogo?: boolean;
    }
  }
}

function RouterProviderWithUserContext() {
  const auth = useAuth();

  const [ theme, storeTheme ] = useLocalStorage(LOCAL_STORAGE.theme.key, LOCAL_STORAGE.theme.initialValue);

  const meQuery = useQuery({ ...userQueries.me, enabled: auth.isAuthenticated });
  const organizationQuery = useQuery({ ...organizationQueries.me, enabled: auth.isAuthenticated });

  // This prevents a jarring flash of the global loader on fast connections where queries resolve almost instantly.
  const delayedQueriesData = useMinDelay(
    !auth.isAuthenticated || auth.isLoading || meQuery.isPending || organizationQuery.isPending,
    { meData: meQuery.data, organizationData: organizationQuery.data },
    700,
  );

  const delayedMeQueryError = useMinDelay(
    meQuery.isPending,
    { isError: meQuery.isError, error: meQuery.error },
    700,
  );

  const prevMeQueryRef = useRef<typeof meQuery.data>(undefined);
  const prevOrganizationQuery = useRef<typeof organizationQuery.data>(undefined);
  useEffect(() => {
    if (delayedQueriesData.pending) {
      return;
    }

    if (
      delayedQueriesData.data.meData !== prevMeQueryRef.current ||
      delayedQueriesData.data.organizationData !== prevOrganizationQuery.current
    ) {
      router.invalidate();
    }

    prevMeQueryRef.current = delayedQueriesData.data.meData;
    prevOrganizationQuery.current = delayedQueriesData.data.organizationData;
  }, [ delayedQueriesData.pending, delayedQueriesData.data ]);

  const prevDelayedMeQueryErrorRef = useRef(delayedMeQueryError.data);
  useEffect(() => {
    if (delayedMeQueryError.pending) {
      return;
    }
    if (delayedMeQueryError.data !== prevDelayedMeQueryErrorRef.current) {
      router.invalidate();
    }
    prevDelayedMeQueryErrorRef.current = delayedMeQueryError.data;
  }, [ delayedMeQueryError.pending, delayedMeQueryError.data ]);

  if (meQuery.data?.original.timezone) {
    timezone.set(meQuery.data.original.timezone);
  }

  let info: { heading: string; details: ReactNode } | undefined = undefined;
  if (
    delayedMeQueryError.data.isError &&
    delayedMeQueryError.data.error instanceof HttpException &&
    delayedMeQueryError.data.error.response?.status === 403
  ) {
    info = delayedMeQueryError.data.error.message === 'Organization is not active' ?
      {
        heading: 'Your organization is no longer active',
        details: (
          <>
          Contact{' '}
            <Link href="mailto:support@trendmd.com" size={14}>TrendMD Support</Link>
            {' '}to reactivate it or get more information
          </>
        ),
      } :
      {
        heading: 'Your account no longer has access to the organization',
        details: 'Contact your team manager to restore access',
      };
  }

  return (
    <RouterProvider
      router={router}
      context={{
        user: delayedQueriesData.data.meData,
        organization: delayedQueriesData.data.organizationData,
        info,
        theme,
        setTheme: (newTheme) => {
          flushSync(() => storeTheme(newTheme));
          router.invalidate();
        },
      }}
    />
  );
}

const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <I18nProvider locale={LOCALE}>
        <AuthProvider {...OIDC_CONFIG}>
          <QueryClientProvider client={queryClient}>
            <RouterProviderWithUserContext />
          </QueryClientProvider>
        </AuthProvider>
      </I18nProvider>
    </StrictMode>,
  );
}

if (process.env.NODE_ENV === 'development') {
  import('react-scan').then(({ scan }) => scan({ enabled: true })).catch(console.error);
}
// To start measuring performance in the app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
